import apiClient from './api';
import type { RestaurantSocialLinks } from '../utils/restaurantSocialLinks';
import { dedupeAsync } from '../utils/requestDedupe';
import type { GuestOrderingStatus } from '../utils/guestOrdering';
import type { GuestOnlinePayments } from '../utils/guestPaymentMethods';

export interface Restaurant {
  id: number;
  appName: string;
  restaurantName: string;
  rating?: number | null;
  pepparRating?: number | null;
  customerReviews?: RestaurantCustomerReviewSummary;
  currencySymbol?: string;
  restaurantAddressDetails?: {
    area?: string;
    city?: string;
    locality?: string;
    landmark?: string;
    state?: string;
    country?: string;
    phone1?: string;
    phone2?: string;
    landline?: string;
    contactPerson?: string;
    emailId?: string;
    pincode?: string;
    lat?: number;
    lng?: number;
    googleMapLink?: string;
  };
  appDesc?: string;
  avgCostForTwo?: string;
  establishedYear?: string;
  /** Listing card image (admin: Restaurant Image); not used on restaurant home banners */
  coverImage?: string;
  /** Brand logo (admin: App Logo) */
  appLogo?: string;
  restaurantLandingDetailsList?: Array<{
    image: string;
    title?: string;
    description?: string;
  }>;
  cuisineList?: Array<{
    id: number;
    name: string;
    iconUrl?: string;
    portraitBannerUrl?: string;
    landscapeBannerUrl?: string;
    displayOrder?: number;
  }>;
  socialLinks?: RestaurantSocialLinks;
  /** FSSAI registration / licence number, when the restaurant has provided one. */
  fssaiNumber?: string;
  /** Restaurant GSTIN, when registered. */
  gstin?: string;
  /** Distance from user when geo search was used (km). */
  distanceKm?: number;
}

export interface RestaurantCustomerReviewSummary {
  averageRating?: number | null;
  reviewCount?: number;
}

export interface RestaurantCustomerReview {
  id: number;
  userName: string;
  rating: number;
  comment?: string | null;
  submittedOn?: string;
  verifiedPurchase?: boolean;
}

export interface RestaurantReviewsResponse {
  success: boolean;
  reviews: RestaurantCustomerReview[];
  summary?: RestaurantCustomerReviewSummary;
  message?: string;
}

export interface DineInTableInfo {
  requestId: number;
  tableId: number;
  tableNumber: number;
  /** All active allotted table numbers when staff assigned more than one. */
  tableNumbers?: number[];
  /** Human-readable numbers only, e.g. "10 and 5" or "4, 7 and 10". */
  tableLabel?: string;
  spaceType?: 'TABLE' | 'ROOM';
  spaceNoun?: string;
  /** Full label including noun, e.g. "Room 101" or "Table 5 and 6". */
  displayLabel?: string;
  allotted: boolean;
  active: boolean;
}

export function spaceNoun(spaceType?: string | null): 'Table' | 'Room' {
  return spaceType === 'ROOM' ? 'Room' : 'Table';
}

export function formatSpaceLabel(
  spaceType: string | undefined | null,
  number: number | string | undefined | null
): string {
  if (number == null || number === '') {
    return spaceNoun(spaceType);
  }
  return `${spaceNoun(spaceType)} ${number}`;
}

export function formatDineInTableLabel(
  info: Pick<DineInTableInfo, 'tableLabel' | 'tableNumbers'> & { tableNumber?: number }
): string {
  if (info.tableLabel && info.tableLabel.trim()) {
    return info.tableLabel.trim();
  }
  if (info.tableNumbers && info.tableNumbers.length > 0) {
    const nums = [...info.tableNumbers].sort((a, b) => a - b);
    if (nums.length === 1) return String(nums[0]);
    if (nums.length === 2) return `${nums[0]} and ${nums[1]}`;
    return nums.slice(0, -1).join(', ') + ' and ' + nums[nums.length - 1];
  }
  if (info.tableNumber == null) {
    return '';
  }
  return String(info.tableNumber);
}

export function formatSpaceOptionLabel(option: {
  tableName?: string;
  displayLabel?: string;
  spaceType?: string | null;
  spaceNoun?: string;
  tableNumber?: number | string;
}): string {
  const number = option.tableNumber;
  const noun = option.spaceNoun || spaceNoun(option.spaceType);
  const fallback =
    (option.displayLabel && option.displayLabel.trim()) ||
    (number != null && number !== '' ? `${noun} ${number}` : noun);
  const name = option.tableName?.trim();
  if (name && name !== 'N/A' && name !== String(number) && name !== fallback) {
    return name;
  }
  return fallback;
}

export function formatAssignedSpaceLabel(
  info: Pick<DineInTableInfo, 'displayLabel' | 'spaceType' | 'tableLabel' | 'tableNumbers'> & {
    tableNumber?: number;
  }
): string {
  if (info.displayLabel && info.displayLabel.trim()) {
    return info.displayLabel.trim();
  }
  return formatSpaceLabel(info.spaceType, formatDineInTableLabel(info));
}

export interface DineInTableOption {
  id: number;
  tableNumber: number;
  tableName?: string;
  totalSeats?: number;
  available: boolean;
  selectable: boolean;
  reserved: boolean;
  canSelect: boolean;
  spaceType?: 'TABLE' | 'ROOM';
  spaceNoun?: string;
  displayLabel?: string;
}

export interface TableLocationOption {
  id: number;
  name: string;
}

export interface RestaurantFilter {
  text?: string;
  lat?: number;
  lng?: number;
  radiusKm?: number;
}

export const DEFAULT_RESTAURANT_SEARCH_RADIUS_KM = 20;

export interface RestaurantResponse {
  success: boolean;
  restaurants: Restaurant[];
  message?: string;
}

export interface PlanFeaturesPayload {
  restaurant?: string[];
  customer?: string[];
  restaurantVisible?: string[];
  customerVisible?: string[];
  discoverable?: boolean;
  platformStatus?: string;
  subscriptionStatus?: string;
  planId?: number;
}

export interface CustomerDisplaySettings {
  showMenuItemImages?: boolean;
  /** Guests may add a cooking request to each line (outlet setting, default on). */
  guestItemNotesEnabled?: boolean;
  /** Guests may add one note for the whole order (outlet setting, default on). */
  guestOrderNoteEnabled?: boolean;
  /** Outlet quick-pick note chips, e.g. "Less spicy", "No onion". */
  notePresets?: string[];
  itemNoteMaxLength?: number;
  orderNoteMaxLength?: number;
}

export interface RestaurantDetailResponse {
  success: boolean;
  restaurant: Restaurant;
  openingHours?: Array<{
    day: string;
    breakfast: string;
    lunch: string;
    dinner: string;
  }>;
  features?: PlanFeaturesPayload;
  guestOrdering?: GuestOrderingStatus;
  onlinePayments?: GuestOnlinePayments;
  displaySettings?: CustomerDisplaySettings;
  message?: string;
}

export interface SizePrice {
  id: number;
  sizeTypeId: number;
  sizeTypeName?: string;
  sizeTypeCode?: string;
  price: number; // Final price after discounts
  mrp?: number; // Maximum Retail Price
  sellingPrice?: number; // Current selling price
  makingPrice?: number; // Cost price
  isAvailable?: boolean;
  displayOrder?: number;
  discountPercent?: number;
  /** Outlet override for the variant name (e.g. "Half plate"); null = size name. */
  displayName?: string | null;
  /** Name to show: displayName, else sizeTypeName. */
  label?: string;
  isDefault?: boolean;
  skuCode?: string;
}

export type ModifierGroupType = 'ADDON' | 'CHOICE' | 'PREFERENCE';

export interface MenuModifierOption {
  id: number;
  optionCode?: string;
  optionName?: string;
  /** Price for variants without an entry in priceBySize. */
  priceDelta?: number;
  /** Per-variant price, keyed by sizeTypeId. */
  priceBySize?: Record<string, number>;
  isAvailable?: boolean;
  /** Variants on which this option is out of stock. */
  unavailableSizeTypeIds?: number[];
  dietTag?: 'VEG' | 'NON_VEG' | 'EGG' | string | null;
  displayOrder?: number;
}

export interface MenuModifierGroup {
  id: number;
  groupCode?: string;
  groupName?: string;
  groupType?: ModifierGroupType | string;
  minSelect?: number;
  /** 0 or less = no limit. */
  maxSelect?: number;
  required?: boolean;
  displayOrder?: number;
  /** Variants this group applies to; empty = every variant. */
  sizeTypeIds?: number[];
  options?: MenuModifierOption[];
}

/** Variant + add-on fields shared by menu list and detail items (design §7). */
export interface MenuItemOptionFields {
  /** Picker heading for the variant axis ("Size", "Portion", …). */
  variantLabel?: string;
  defaultSizeTypeId?: number;
  /** Add-on groups, or more than one variant: tapping add opens the options sheet. */
  hasOptions?: boolean;
  hasRequiredOptions?: boolean;
  modifierGroups?: MenuModifierGroup[];
}

export interface GravyTypeOption {
  id: number;
  typeCode: string;
  typeName?: string;
}

export interface SpicinessLevelOption {
  id: number;
  levelCode: string;
  levelName?: string;
}

export interface MenuItem extends MenuItemOptionFields {
  id: number;
  item: string;
  skuCode?: string;
  itemCode?: number;
  itemUrl?: string;
  description: string;
  price: number; // Selling price (for backward compatibility, use sellingPrice if available)
  mrp?: number; // Maximum Retail Price
  sellingPrice?: number; // Current selling price (after discounts)
  makingPrice?: number; // Cost price
  sizePrices?: SizePrice[]; // Size-based pricing
  customized?: boolean;
  gravyTypes?: GravyTypeOption[];
  spicinessLevels?: SpicinessLevelOption[];
  categoryId: number;
  cuisineId: number;
  imageBig: string;
  imageSmall?: string;
  itemType: 'veg' | 'nonveg';
  rating?: number;
  discount?: number;
  discountPercent?: number;
  offer?: number;
  category?: string;
  cuisine?: string;
  availability?: boolean;
  qty?: number;
  tags?: string[];
  inWishlist?: boolean;
  wishlistId?: number;
  specialItem?: boolean;
  promotionId?: number;
  promotionName?: string;
  promotionBadge?: string;
  promotionalPrice?: number;
  promotionalPercentOff?: number;
}

export interface CatalogItem {
  id: number;
  name: string;
  type?: string;
  iconUrl?: string;
  portraitBannerUrl?: string;
  landscapeBannerUrl?: string;
  displayOrder?: number;
  itemCount?: number;
}

export interface CatalogResponse {
  success: boolean;
  categories: CatalogItem[];
  cuisines: CatalogItem[];
  message?: string;
}

export interface MenuResponse {
  success: boolean;
  restaurant: Restaurant;
  menu: {
    menuList: MenuItem[];
    categoryList: Record<number, string>;
    cuisineList: Record<number, string>;
  };
  categories: Array<{
    id: number;
    type: string;
    name?: string;
    iconUrl?: string;
    portraitBannerUrl?: string;
    landscapeBannerUrl?: string;
    displayOrder?: number;
  }>;
  cuisines: Array<{
    id: number;
    name: string;
    iconUrl?: string;
    portraitBannerUrl?: string;
    landscapeBannerUrl?: string;
    displayOrder?: number;
  }>;
  displaySettings?: CustomerDisplaySettings;
  message?: string;
}

export interface MenuRecommendationsPayload {
  bestSelling: MenuItem[];
  todaysSpecial: MenuItem[];
  frequentlyBoughtTogether: MenuItem[];
}

export interface MenuRecommendationsResponse {
  success: boolean;
  recommendations?: MenuRecommendationsPayload;
  message?: string;
}

class RestaurantService {
  /**
   * Get list of restaurants with optional filter
   */
  async getRestaurants(filter?: RestaurantFilter): Promise<RestaurantResponse> {
    const params: Record<string, string> = {};
    if (filter && Object.keys(filter).length > 0) {
      params.filter = JSON.stringify(filter);
    }

    const response = await apiClient.get('/api/restaurants', { params });
    return response.data;
  }

  /**
   * Get restaurant details by appName
   */
  async getRestaurant(appName: string): Promise<RestaurantDetailResponse> {
    return dedupeAsync(`restaurant:${appName}`, async () => {
      const response = await apiClient.get(`/api/restaurants/${appName}`);
      return response.data;
    }, 15_000);
  }

  async getRestaurantReviews(
    appName: string,
    limit = 20,
    offset = 0
  ): Promise<RestaurantReviewsResponse> {
    const response = await apiClient.get(`/api/restaurants/${appName}/reviews`, {
      params: { limit, offset },
    });
    return response.data;
  }

  async getPublicCoupons(appName: string): Promise<{
    success: boolean;
    coupons: Array<{
      id: number;
      couponCode?: string;
      couponType?: string;
      discount?: number;
      minAmount?: number;
      maxDiscountAmount?: number;
      description?: string;
      orderType?: string;
    }>;
    message?: string;
  }> {
    const response = await apiClient.get(`/api/restaurants/${appName}/coupons/public`);
    return response.data;
  }

  /**
   * Get restaurant menu by appName
   */
  async getRestaurantMenu(appName: string, sortBy?: string): Promise<MenuResponse> {
    const params: any = {};
    if (sortBy && sortBy !== 'default') {
      params.sortBy = sortBy;
    }
    const response = await apiClient.get(`/api/restaurants/${appName}/menu`, { params });
    return response.data;
  }

  async getMenuRecommendations(
    appName: string,
    menuIds?: number[],
    limit = 6
  ): Promise<MenuRecommendationsResponse> {
    const params: { limit: number; menuIds?: string } = { limit };
    if (menuIds && menuIds.length > 0) {
      params.menuIds = menuIds.join(',');
    }
    const response = await apiClient.get(`/api/restaurants/${appName}/menu/recommendations`, { params });
    return response.data;
  }

  /**
   * Lightweight catalog for home sliders (categories/cuisines with images + counts).
   */
  async getRestaurantCatalog(appName: string): Promise<CatalogResponse> {
    const response = await apiClient.get(`/api/restaurants/${appName}/catalog`);
    return response.data;
  }

  /**
   * Filter menu items
   */
  async filterMenu(
    appName: string,
    filter: {
      itemType?: string;
      categories?: number[];
      cuisines?: number[];
      tags?: string[];
      priceFilterEnabled?: boolean;
      minPrice?: number;
      maxPrice?: number;
      sortBy?: string;
    }
  ): Promise<{ success: boolean; menuItems: MenuItem[]; message?: string }> {
    const response = await apiClient.post(
      `/api/restaurants/${appName}/menu/filter`,
      filter
    );
    return response.data;
  }

  /**
   * Get wishlist menu items
   */
  async getWishlistMenu(
    appName: string
  ): Promise<{ success: boolean; menuItems: MenuItem[]; message?: string; requiresLogin?: boolean }> {
    const response = await apiClient.get(
      `/api/restaurants/${appName}/menu/wishlist`
    );
    return response.data;
  }

  /**
   * Get menu item details by itemUrl
   */
  async getMenuItemDetail(
    appName: string,
    itemUrl: string
  ): Promise<{ success: boolean; menuItem: MenuItemDetail; message?: string }> {
    const response = await apiClient.get(
      `/api/restaurants/${appName}/menu/item/${itemUrl}`
    );
    return response.data;
  }

  /**
   * Get reviews for a menu item
   */
  async getMenuItemReviews(
    appName: string,
    itemUrl: string
  ): Promise<{ success: boolean; reviews: Review[]; message?: string }> {
    const response = await apiClient.get(
      `/api/restaurants/${appName}/menu/item/${itemUrl}/reviews`
    );
    return response.data;
  }

  /**
   * Check if logged-in user can review a menu item
   */
  async getMenuItemReviewEligibility(
    appName: string,
    itemUrl: string
  ): Promise<{
    success: boolean;
    eligible?: boolean;
    reason?: string;
    message?: string;
  }> {
    const response = await apiClient.get(
      `/api/restaurants/${appName}/menu/item/${itemUrl}/reviews/eligibility`
    );
    return response.data;
  }

  /**
   * Save a review for a menu item
   */
  async saveMenuItemReview(
    appName: string,
    itemUrl: string,
    review: SaveReviewRequest
  ): Promise<{ success: boolean; message?: string }> {
    const response = await apiClient.post(
      `/api/restaurants/${appName}/menu/item/${itemUrl}/reviews`,
      review
    );
    return response.data;
  }

  /**
   * Current dine-in table assignment for the logged-in user
   */
  async getDineInTableStatus(appName: string): Promise<{
    success: boolean;
    hasTable?: boolean;
    dineInTable?: DineInTableInfo;
    message?: string;
  }> {
    const response = await apiClient.get(`/api/restaurants/${appName}/dine-in/table-status`);
    return response.data;
  }

  /**
   * Tables available for dine-in selection (staff approval still required after request)
   */
  async getDineInTables(
    appName: string,
    noOfPerson?: number
  ): Promise<{
    success: boolean;
    tables?: DineInTableOption[];
    allowManualTableEntry?: boolean;
    hasRooms?: boolean;
    hasTables?: boolean;
    message?: string;
  }> {
    const params = noOfPerson != null ? { noOfPerson } : undefined;
    const response = await apiClient.get(`/api/restaurants/${appName}/dine-in/tables`, { params });
    return response.data;
  }

  /**
   * Locations/areas for table booking preference.
   * Returns active locations; if none exist, the backend may return a single default (Main Area).
   */
  async getTableLocations(appName: string): Promise<{
    success: boolean;
    locations?: TableLocationOption[];
    defaultLocationId?: number;
    message?: string;
  }> {
    const response = await apiClient.get(`/api/restaurants/${appName}/table-locations`);
    return response.data;
  }

  /**
   * Request a dine-in table (allotted unless auto-approve is off and staff must confirm)
   */
  async createDineInTableRequest(
    appName: string,
    tableId: number
  ): Promise<{
    success: boolean;
    requestId?: number;
    allotted?: boolean;
    tableNumber?: number;
    tableId?: number;
    spaceType?: 'TABLE' | 'ROOM';
    spaceNoun?: string;
    displayLabel?: string;
    tableAlreadyExists?: boolean;
    redirectToJoin?: boolean;
    redirectToMenu?: boolean;
    orderId?: number;
    message?: string;
  }> {
    const response = await apiClient.post(`/api/restaurants/${appName}/dine-in/table-request`, {
      tableId,
    });
    return response.data;
  }

  async createDineInTableRequestByNumber(
    appName: string,
    tableNumber: number
  ): Promise<{
    success: boolean;
    requestId?: number;
    allotted?: boolean;
    tableNumber?: number;
    tableId?: number;
    spaceType?: 'TABLE' | 'ROOM';
    spaceNoun?: string;
    displayLabel?: string;
    tableAlreadyExists?: boolean;
    redirectToJoin?: boolean;
    redirectToMenu?: boolean;
    orderId?: number;
    message?: string;
  }> {
    const response = await apiClient.post(`/api/restaurants/${appName}/dine-in/table-request`, {
      tableNumber,
    });
    return response.data;
  }

  /**
   * Create table request from QR code token
   */
  async createTableRequestFromQR(
    appName: string,
    token: string
  ): Promise<{
    success: boolean;
    requiresLogin?: boolean;
    requestId?: number;
    allotted?: boolean;
    tableNumber?: number;
    spaceType?: 'TABLE' | 'ROOM';
    spaceNoun?: string;
    displayLabel?: string;
    message?: string;
    tableAlreadyExists?: boolean;
    redirectToJoin?: boolean;
    redirectToMenu?: boolean;
    orderId?: number;
    tableId?: number;
  }> {
    const response = await apiClient.post(
      `/api/restaurants/${appName}/table-request-from-qr`,
      { token }
    );
    return response.data;
  }

  /**
   * Get order details by orderId
   */
  async getOrderDetails(
    appName: string,
    orderId: number
  ): Promise<OrderDetailsResponse> {
    return dedupeAsync(`order-details:${appName}:${orderId}`, async () => {
      const response = await apiClient.get(`/api/restaurants/${appName}/orders/${orderId}`);
      return response.data;
    }, 2_000);
  }

  /**
   * Submit dine-in quick assistance request (waiter, water, bill, etc.)
   */
  async submitUserServiceRequest(
    appName: string,
    orderId: number,
    service: string
  ): Promise<{ success: boolean; message?: string }> {
    const response = await apiClient.get(
      `/api/restaurants/${appName}/orders/${orderId}/service-request`,
      { params: { service } }
    );
    return response.data;
  }

  /**
   * Submit a pre-order table assistance request (no order required — uses active table allocation).
   * Used for Dine-In guests after table allocation but before placing an order.
   */
  async submitTableAssistance(
    appName: string,
    service: string
  ): Promise<{ success: boolean; message?: string }> {
    const response = await apiClient.post(
      `/api/restaurants/${appName}/table-assistance`,
      null,
      { params: { service } }
    );
    return response.data;
  }

  /**
   * Place an order from the cart
   */
  async placeOrder(
    appName: string,
    orderType: number,
    orderId?: number,
    extras?: {
      quoteId?: string;
      addressId?: number;
      paymentMethod?: string;
      kitchenNote?: string;
      orderSource?: string;
      kioskSessionId?: string | number;
      kioskTableId?: number;
    }
  ): Promise<{
    success: boolean;
    orderId?: number;
    message?: string;
    code?: string;
    redirectTo?: string;
    existingOrderId?: number;
    queueTokenLabel?: string;
  }> {
    try {
      const requestBody: any = { type: orderType };
      if (orderId) {
        requestBody.order = orderId;
      }
      if (extras?.quoteId) {
        requestBody.quoteId = extras.quoteId;
      }
      if (extras?.addressId) {
        requestBody.addressId = extras.addressId;
      }
      if (extras?.paymentMethod) {
        requestBody.paymentMethod = extras.paymentMethod;
      }
      if (extras?.kitchenNote && extras.kitchenNote.trim()) {
        requestBody.kitchenNote = extras.kitchenNote.trim();
      }
      if (extras?.orderSource) {
        requestBody.orderSource = extras.orderSource;
      }
      if (extras?.kioskSessionId != null && extras.kioskSessionId !== '') {
        requestBody.kioskSessionId = extras.kioskSessionId;
      }
      if (extras?.kioskTableId != null) {
        requestBody.kioskTableId = extras.kioskTableId;
      }
      const response = await apiClient.post(`/api/restaurants/${appName}/place-order`, requestBody);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get user's orders for a restaurant
   */
  async getMyOrders(
    appName: string,
    type?: 'ongoing' | 'history'
  ): Promise<{
    success: boolean;
    orders?: UserOrderDetails[];
    count?: number;
    message?: string;
  }> {
    try {
      const params: any = {};
      if (type) {
        params.type = type;
      }
      const response = await apiClient.get(`/api/restaurants/${appName}/my-orders`, { params });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async getAllMyOrders(type?: 'ongoing' | 'history'): Promise<{
    success: boolean;
    orders?: UserOrderDetails[];
    count?: number;
    message?: string;
  }> {
    const params: Record<string, string> = {};
    if (type) {
      params.type = type;
    }
    const response = await apiClient.get('/api/user/orders', { params });
    return response.data;
  }

  /**
   * Check if user has an ongoing order for a restaurant
   */
  async getOngoingOrder(
    appName: string
  ): Promise<{
    success: boolean;
    hasOngoingOrder?: boolean;
    order?: UserOrderDetails;
    orderId?: number;
    message?: string;
  }> {
    return dedupeAsync(`ongoing-order:${appName}`, async () => {
      const response = await apiClient.get(`/api/restaurants/${appName}/ongoing-order`);
      return response.data;
    }, 5_000);
  }

  async getOrderFeedback(
    appName: string,
    orderId: number
  ): Promise<OrderFeedbackContextResponse> {
    const response = await apiClient.get(`/api/restaurants/${appName}/orders/${orderId}/feedback`);
    return response.data;
  }

  async submitOrderFeedback(
    appName: string,
    orderId: number,
    payload: OrderFeedbackFormData
  ): Promise<OrderFeedbackSubmitResponse> {
    const response = await apiClient.post(
      `/api/restaurants/${appName}/orders/${orderId}/feedback`,
      payload
    );
    return response.data;
  }

  async updateOrderFeedback(
    appName: string,
    orderId: number,
    payload: OrderFeedbackFormData
  ): Promise<OrderFeedbackSubmitResponse> {
    const response = await apiClient.put(
      `/api/restaurants/${appName}/orders/${orderId}/feedback`,
      payload
    );
    return response.data;
  }
}

export interface MenuItemDetail extends MenuItemOptionFields {
  id: number;
  item: string;
  skuCode?: string;
  itemCode?: number;
  itemUrl: string;
  description?: string;
  price: number; // Selling price (for backward compatibility, use sellingPrice if available)
  mrp?: number; // Maximum Retail Price
  sellingPrice?: number; // Current selling price (after discounts)
  discountPercent?: number;
  makingPrice?: number; // Cost price
  sizePrices?: SizePrice[]; // Size-based pricing
  customized?: boolean;
  gravyTypes?: GravyTypeOption[];
  spicinessLevels?: SpicinessLevelOption[];
  rating?: number;
  itemType?: string;
  availability?: boolean;
  qty?: number;
  inWishlist?: boolean;
  wishlistId?: number;
  images?: Array<{
    id: number;
    name: string;
    mimeType?: string;
    primaryCheck?: boolean;
  }>;
  ingredients?: Array<{
    id: number;
    ingredientName: string;
    visibility: boolean;
  }>;
  tags?: string[];
  preparationTime?: number;
  category?: string;
  cuisine?: string;
  categoryId?: number;
  cuisineId?: number;
  restaurant?: {
    id: number;
    appName: string;
    restaurantName?: string;
    currencySymbol?: string;
  };
  promotionId?: number;
  promotionName?: string;
  promotionBadge?: string;
  promotionalPrice?: number;
  promotionalPercentOff?: number;
}

export interface Review {
  id: number;
  userName: string;
  emailId: string;
  comment: string;
  rating: number;
  createdOn: string;
}

export interface SaveReviewRequest {
  comment: string;
  rating: number;
  title?: string;
}

export interface OrderItem {
  id: number;
  menuId: number;
  quantity: number;
  itemName: string;
  sizeTypeId?: number;
  price: number;
  totalPrice: number;
  /** Variant and add-ons as sold, parts joined with " | ". */
  optionsNote?: string | null;
  /** Guest / staff cooking request for this line. */
  itemNote?: string | null;
  /** Guest who added this line on a shared order. Empty when the restaurant added it. */
  userId?: number;
  addedByName?: string;
  /** True when restaurant staff added this line after the guest placed the order. */
  addedByStaff?: boolean;
  /** Staff member who added the line, or "Restaurant" when that person is unknown. */
  addedByStaffName?: string;
  modifierSignature?: string;
  /** Add-on amount per unit (already included in price). */
  modifierAmount?: number;
}

export interface UserOrder {
  id: number;
  orderCode?: string; // Custom order code (e.g., MB00027A1B2C)
  orderType: number;
  orderStatus: string;
  grandTotal: number;
  subTotal: number;
  tableNumber?: number;
  spaceType?: 'TABLE' | 'ROOM';
  spaceNoun?: string;
  spaceLabel?: string;
  waitDateTime?: string;
  approxWaitTime?: number;
  estimatedPrepMinutes?: number;
  assignedTo?: number;
  restroId: number;
  currency?: string;
  currencySymbol?: string;
  paymentStatus?: string;
  paymentMethod?: string;
  createdOn?: string;
  updatedOn?: string;
  sharePin?: string; // 4-digit PIN for sharing order
  isSharedOrder?: boolean; // Whether order can be joined by others
  promotionId?: number;
  promotionDiscountAmount?: number;
  promotionName?: string;
  /** Note for the kitchen for the whole order. */
  kitchenNote?: string | null;
}

export interface UserRestaurantTable {
  id: number;
  tableNumber: number;
  tableId: number;
  allotted: boolean;
  active: boolean;
  spaceType?: 'TABLE' | 'ROOM';
  spaceNoun?: string;
  spaceLabel?: string;
}

export interface BillingDetails {
  discount?: number;
  discountAmount?: number;
  promotionDiscountAmount?: number;
  couponDiscountAmount?: number;
  subTotal?: number;
  grandTotal?: number;
  stateGST?: number;
  stateGSTCharges?: number;
  centralGST?: number;
  centralGSTCharges?: number;
  packingCharges?: number;
  deliveryCharges?: number;
  serviceTax?: number;
  serviceTaxCharges?: number;
  currencySymbol?: string;
}

export interface OrderDetails {
  orderId: number;
  userOrder: UserOrder;
  userOrderItemsList: OrderItem[];
  menuCardList: Array<{
    id: number;
    item: string;
    itemUrl: string;
  }>;
  userRestaurantTable?: UserRestaurantTable;
  billingDetails?: BillingDetails;
  assignedUser?: {
    id: number;
    userName: string;
  };
  userDeliveryDetails?: {
    fullName?: string;
    phone?: string;
    addressType?: string;
    floor?: string;
    tower?: string;
    building?: string;
    area?: string;
    locality?: string;
    city?: string;
    pincode?: string;
    landmark?: string;
  };
  userOrderDeliveryDetails?: {
    destination?: string;
  };
}

export interface OrderDetailsResponse {
  success: boolean;
  orderDetails?: OrderDetails;
  feedbackSummary?: OrderFeedbackSummary;
  activePromotions?: Array<{
    id: number;
    name: string;
    description?: string;
    discountPreview?: string;
  }>;
  orderPromotionPreview?: {
    promotionId: number;
    promotionName: string;
    estimatedDiscount: number;
    discountPreview?: string;
  };
  itemPromotionBadges?: Record<
    number,
    { promotionName?: string; promotionBadge?: string }
  >;
  menuRecommendations?: MenuRecommendationsPayload;
  canPayOnline?: boolean;
  /** Successful guest online captures on this order (rupees). */
  onlinePaidAmount?: number;
  /** Remaining guest online balance (rupees). */
  onlineBalanceDue?: number;
  onlinePayments?: GuestOnlinePayments;
  restaurantName?: string;
  restaurantPhone?: string;
  invoiceDownload?: GuestInvoiceDownloadPayload;
  queueTokenLabel?: string;
  message?: string;
}

export interface GuestInvoiceDownloadOption {
  invoiceId: number;
  invoiceNo?: string;
  label?: string;
}

export interface GuestInvoiceDownloadPayload {
  available?: boolean;
  invoices?: GuestInvoiceDownloadOption[];
}

export interface OrderFeedbackSummary {
  eligible: boolean;
  submitted: boolean;
  daysRemaining: number;
  canEdit?: boolean;
}

export interface OrderFeedbackItemRating {
  menuId: number;
  rating?: number;
  comment?: string;
  itemName?: string;
}

export interface OrderFeedbackFormData {
  overallRating: number;
  restaurantRating?: number;
  foodQualityRating?: number;
  ambienceRating?: number;
  captainRating?: number;
  valueForMoneyRating?: number;
  orderAccuracyRating?: number;
  speedRating?: number;
  packagingRating?: number;
  deliveryRating?: number;
  billingRating?: number;
  recommendScore?: number | null;
  comment?: string;
  itemRatings: OrderFeedbackItemRating[];
}

export interface OrderFeedbackContext extends OrderFeedbackSummary {
  applicableFields?: Record<string, boolean>;
  orderType?: number;
  captain?: { id: number; userName: string };
  orderItems?: Array<{ menuId: number; itemName?: string }>;
  feedback?: OrderFeedbackFormData & { submittedAt?: string; updatedAt?: string };
}

export interface OrderFeedbackContextResponse extends OrderFeedbackContext {
  success: boolean;
  message?: string;
}

export interface OrderFeedbackSubmitResponse {
  success: boolean;
  message?: string;
  feedbackSummary?: OrderFeedbackSummary;
  feedbackId?: number;
}

export interface UserOrderDetails {
  orderId: number;
  userOrder: UserOrder;
  userOrderItemsList: OrderItem[];
  menuCardList?: MenuItem[];
  billingDetails?: BillingDetails;
  userRestaurantTable?: UserRestaurantTable;
  assignedUser?: {
    id: number;
    userName: string;
  };
  feedbackSummary?: OrderFeedbackSummary;
  restaurantName?: string;
  restaurantAppName?: string;
  userDeliveryDetails?: {
    fullName?: string;
    phone?: string;
    addressType?: string;
    floor?: string;
    tower?: string;
    building?: string;
    area?: string;
    locality?: string;
    city?: string;
    pincode?: string;
    landmark?: string;
  };
  userOrderDeliveryDetails?: {
    destination?: string;
  };
  invoiceDownload?: GuestInvoiceDownloadPayload;
}

export default new RestaurantService();

